import type { ReactNode } from "react";

// The shared list-page header (Session AQT, Phase 1 design system foundation): title + optional
// subtitle + optional right-aligned action row, replacing nine hand-rolled <h1> blocks that had
// each independently converged on roughly this same shape (QuotesPage, InvoicesPage,
// ContractsPage, TimesheetsPage, LolerReportsPage, ComplaintsPage, and others).
//
// Deliberately scoped to LIST pages only. Detail pages (CustomerDetailPage's live-editable name,
// ProjectDetailPage's click-to-rename h1 with no back link, DefectDetailPage's single-row
// h1+badge+status with no subtitle) carry real, working variance that is not this shape -- do not
// reach for this component there. That rebuild belongs to Phase 2, alongside the record pages.

export interface PageHeaderProps {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** A small uppercase line above the title (e.g. "Compliance"). Session AUJ. */
  eyebrow?: ReactNode;
}

export function PageHeader({ title, subtitle, actions, className = "", eyebrow }: PageHeaderProps) {
  return (
    <div
      className={`flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between ${className}`.trim()}
    >
      {/* The title block gives way FIRST (a huge shrink weight, down to 12rem) so the actions keep their
          natural width on a desktop; the actions shrink only after that, and never below their widest
          control (min-w-min on the slot), where a long filter row wraps (Session BDU). */}
      <div className="min-w-0 sm:min-w-[12rem] sm:[flex-shrink:999]">
        {eyebrow != null && (
          <p className="mb-1 font-menu text-xs uppercase tracking-widest text-ele-body opacity-70">{eyebrow}</p>
        )}
        <h1 className="font-heading text-3xl tracking-wide text-ele-strong">{title}</h1>
        {subtitle != null && <p className="mt-1 text-sm text-ele-body">{subtitle}</p>}
      </div>
      {actions != null && (
        <div className="flex min-w-min max-w-full flex-wrap items-center gap-2 [&>a]:whitespace-nowrap [&>button]:whitespace-nowrap">{actions}</div>
      )}
    </div>
  );
}
